<template>
  <div class="flex w-full" v-bind="getPt('root', passThrough, ptCtx)">
    <input
      type="file"
      ref="uploader"
      @change="handleFile"
      :multiple="multiple"
      :accept="acceptedFileFormats"
      class="hidden"
      v-bind="getPt('input', passThrough, ptCtx)"
    />
    <div class="w-full" v-bind="getPt('container', passThrough, ptCtx)">
      <div
        class="flex flex-col w-full"
        v-bind="getPt('dropzoneWrapper', passThrough, ptCtx)"
      >
        <div
          class="w-full group"
          :class="[
            {
              'cursor-no-drop opacity-60': disable,
              'cursor-pointer': !disable,
            },
            getPt('dropzone', passThrough, ptCtx)?.class,
          ]"
          @click="
            () => {
              pickFile();
            }
          "
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @dragenter.prevent
          @drop.prevent="
            (e) => {
              emit('selectFiles');
              handleFile(e, true);
              isDragging = false;
            }
          "
          @mouseenter="isHovering = true"
          @mouseleave="isHovering = false"
        >
          <slot
            name="uploadAreaContent"
            :is-dragging="isDragging"
            :is-hovering="isHovering"
            :disable="disable"
            :accepted-file-formats="acceptedFileFormats"
            :max-file-limit="maxFileLimit"
          >
            <div
              class="flex flex-col items-center w-full h-full justify-center"
              v-bind="getPt('uploadArea', passThrough, ptCtx)"
            >
              <MeridiaIcon
                class="icon-m transition-all duration-300"
                :class="[
                  {
                    'text-neutral-text-body': !isDragging,
                    'text-primary-surface-default': isDragging,
                    'group-hover:text-primary-surface-default': !disable,
                  },
                  getPt('uploadIcon', passThrough, ptCtx)?.class,
                ]"
                name="cloud_upload"
                type="material-icons-outlined"
              />
              <transition name="fade" mode="out-in">
                <div
                  v-if="!isDragging"
                  class="text-neutral-text-body text-body-large transition-all mt-2"
                  :class="getPt('uploadText', passThrough, ptCtx)?.class"
                >
                  {{
                    $t(
                      'meridia_components.file_upload.select_or_drag_and_drop_files_to_upload'
                    )
                  }}
                </div>
                <div
                  v-else
                  class="text-neutral-text-body text-body-large transition-all mt-2"
                  :class="getPt('uploadText', passThrough, ptCtx)?.class"
                >
                  {{ $t('meridia_components.file_upload.drop_files_here') }}
                </div>
              </transition>
              <div
                class="text-neutral-border-disabled text-body-small mt-xs"
                :class="getPt('uploadSubtitle', passThrough, ptCtx)?.class"
              >
                {{
                  $t('meridia_components.file_upload.upload_subtitle', {
                    formats: acceptedFileFormats,
                    limit: maxFileLimit / 1024 / 1024,
                  })
                }}
              </div>
              <MeridiaButton
                :type="'tertiary'"
                :size="'base'"
                :label="$t('meridia_components.file_upload.upload_file')"
                class="mt-2xs"
                :class="getPt('uploadButton', passThrough, ptCtx)?.class"
              />
            </div>
          </slot>
        </div>
      </div>
      <transition-group
        name="fade"
        tag="div"
        class="gap-y-xs flex flex-wrap"
        :class="getPt('list', passThrough, ptCtx)?.class"
      >
        <div
          v-for="(fileObject, index) in files"
          :key="fileObject.id"
          class="p-3 w-full flex justify-center items-center radius-base bg-white"
          :class="
            getPt('item', passThrough, { ...ptCtx, file: fileObject, index })
              ?.class
          "
        >
          <div
            class="inline-flex justify-between w-full items-center"
            v-bind="
              getPt('itemContent', passThrough, {
                ...ptCtx,
                file: fileObject,
                index,
              })
            "
          >
            <div
              class="inline-flex items-center mr-xs flex-1 overflow-hidden"
              v-bind="
                getPt('itemInfo', passThrough, {
                  ...ptCtx,
                  file: fileObject,
                  index,
                })
              "
            >
              <transition name="fade" mode="out-in" :duration="150">
                <MeridiaIcon
                  v-if="fileObject.status === 'success'"
                  class="icon-s text-primary-surface-default p-xs"
                  :class="
                    getPt('statusIcon', passThrough, {
                      ...ptCtx,
                      file: fileObject,
                      index,
                    })?.class
                  "
                  name="check"
                  type="material-icons-outlined"
                />
                <MeridiaIcon
                  v-else-if="fileObject.status === 'error'"
                  class="icon-s text-critical-surface-default p-xs"
                  :class="
                    getPt('statusIcon', passThrough, {
                      ...ptCtx,
                      file: fileObject,
                      index,
                    })?.class
                  "
                  name="block"
                  type="material-icons-outlined"
                />
                <MeridiaIcon
                  v-else-if="fileObject.status === 'pending'"
                  class="icon-s text-primary-surface-default p-xs animate-spin"
                  :class="
                    getPt('statusIcon', passThrough, {
                      ...ptCtx,
                      file: fileObject,
                      index,
                    })?.class
                  "
                  name="progress_activity"
                  type="material-symbols-outlined"
                />
              </transition>
              <span
                class="text-neutral-text-body text-body-base truncate"
                :class="
                  getPt('fileName', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })?.class
                "
                :title="fileObject.file.name"
                >{{ fileObject.file.name }}</span
              >
              <div
                class="h-m w-px bg-neutral-border-subtle mx-2xs"
                :class="
                  getPt('divider', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })?.class
                "
              />
              <span
                v-if="fileObject.status === 'success'"
                class="text-neutral-border-disabled text-body-base whitespace-nowrap"
                :class="
                  getPt('fileSize', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })?.class
                "
                >{{ humanStorageSize(fileObject.file.size) }}</span
              >
              <span
                v-else-if="fileObject.status === 'error'"
                class="text-critical-label-default text-body-base whitespace-nowrap"
                :class="
                  getPt('errorText', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })?.class
                "
                >{{ humanStorageSize(fileObject.file.size) }} •
                <!-- `limit` is offered to every reason and used by the ones that name
                     it: a refused file has to say what it was measured against. -->
                {{
                  fileObject.reason
                    ? $t(
                        'meridia_components.file_upload.' + fileObject.reason,
                        {
                          limit: maxFileLimit / 1024 / 1024,
                        }
                      )
                    : $t('meridia_components.file_upload.upload_failed')
                }}</span
              >
              <slot name="itemMeta" :file-object="fileObject" />
            </div>
            <div
              class="inline-flex items-center"
              v-bind="
                getPt('itemActions', passThrough, {
                  ...ptCtx,
                  file: fileObject,
                  index,
                })
              "
            >
              <MeridiaButton
                v-if="fileObject.status === 'success' && !disableDownload"
                :type="'tertiary'"
                :size="'base'"
                icon="open_in_new"
                v-bind="
                  getPt('downloadButton', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })
                "
                @click.prevent="handleDownload(fileObject)"
              />
              <MeridiaButton
                v-if="fileObject.status === 'pending'"
                :type="'tertiary'"
                :size="'base'"
                v-bind="
                  getPt('cancelButton', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })
                "
                @click.prevent="cancelUpload(fileObject)"
              >
                <template #customIcon>
                  <MeridiaIcon
                    name="cancel"
                    type="material-symbols-outlined"
                    style="font-size: inherit"
                  />
                </template>
              </MeridiaButton>
              <MeridiaButton
                v-else
                :type="'tertiary'"
                :size="'base'"
                v-bind="
                  getPt('deleteButton', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })
                "
                @click.prevent="deleteFile($event, fileObject)"
              >
                <template #customIcon>
                  <MeridiaIcon
                    name="delete"
                    type="material-symbols-outlined"
                    style="font-size: inherit"
                  />
                </template>
              </MeridiaButton>
            </div>
          </div>
        </div>
      </transition-group>
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, VNode } from 'vue';
import { MeridiaFileType, PassThroughMeridiaFileUpload } from '.';
import MeridiaButton from '../button/MeridiaButton.vue';
import MeridiaIcon from '../icon/MeridiaIcon.vue';
import { usePassThrough } from 'primevue/passthrough';
import { getPt } from '../utils';

/**
 * Holds the selected files for upload.
 * @type {Ref<MeridiaFileType>}
 */
const files = defineModel<MeridiaFileType[]>({ required: true });

const props = withDefaults(
  defineProps<{
    acceptedFileFormats: string;
    maxFileLimit?: number | undefined;
    disable?: boolean;
    /** Accept more than one file per selection. Off for a single-document slot. */
    multiple?: boolean;
    disableDownload?: boolean;
    /** Per-element passthrough — see PassThroughMeridiaFileUpload. */
    pt?: PassThroughMeridiaFileUpload;
  }>(),
  {
    maxFileLimit: 5368709120,
    disableDownload: false,
    multiple: true,
  }
);

const emit = defineEmits<{
  (
    event: 'createFile',
    file: File,
    callback: (response: {
      status: boolean;
      reason: string | undefined;
      /** Restamp the row id (consumers with numeric backend ids). Omit/undefined
       *  to keep the existing client id. */
      id: number | undefined;
      /** Backend resource id (e.g. UUID) stored on the row for later delete. */
      serverId?: string;
    }) => Promise<void>
  ): void;
  (
    event: 'deleteFile',
    fileObject: MeridiaFileType,
    callback: (response: boolean) => Promise<void>
  ): void;
  (
    event: 'cancelUpload',
    fileObject: MeridiaFileType,
    callback: (response: boolean) => Promise<void>
  ): void;
  (
    event: 'downloadFile',
    fileObject: MeridiaFileType,
    callback: (response: boolean) => Promise<void>
  ): void;
  /** Dropzone click and drag-and-drop. Separate from the root `click`, which the
   *  delete/cancel/download row buttons also fire. */
  (event: 'selectFiles'): void;
}>();

const slots = defineSlots<{
  uploadAreaContent: (props: {
    isDragging: boolean;
    isHovering: boolean;
    disable: boolean;
    acceptedFileFormats: string;
    maxFileLimit: number;
  }) => VNode;
  footer: () => VNode;
  /** Extra per-row metadata rendered after the size/error text (e.g. a date). */
  itemMeta: (props: { fileObject: MeridiaFileType }) => VNode;
}>();

// Merge consumer `pt` over the (empty) defaults; `getPt(section, passThrough, ctx)`
// then resolves each section in the template (calling function-form sections with
// `ctx`). Mirrors the other Meridia inputs.
const passThrough = usePassThrough(
  props.pt,
  {} as PassThroughMeridiaFileUpload,
  { mergeSections: false, mergeProps: true }
);

const isDragging = ref(false);
const isHovering = ref(false);
/** Row ids whose delete is awaiting the consumer's callback. Not rendered, so not reactive. */
const deletingFileIds = new Set<MeridiaFileType['id']>();

/**
 * Live context handed to function-form passthrough sections (dropzone-level).
 * Row sections additionally receive `{ file, index }`, spread in at the call site.
 */
const ptCtx = computed(() => ({
  isDragging: isDragging.value,
  isHovering: isHovering.value,
  disable: !!props.disable,
  acceptedFileFormats: props.acceptedFileFormats,
  maxFileLimit: props.maxFileLimit,
}));

/**
 * Holds the uploader instance.
 * @type {Ref<any>}
 */
const uploader = ref<any>(null);

/**
 * Function to open the file picker dialog.
 */
function pickFile() {
  if (props.disable) return;
  emit('selectFiles');
  uploader.value.click();
}

function checkSizeValid(auxFiles: FileList) {
  const MAX_SIZE = props.maxFileLimit;
  const EMPTY = 0;
  const filesFormatted = Array.from(auxFiles);

  return filesFormatted.some((file) => {
    if (file.size === EMPTY) {
      files.value.push({
        status: 'error',
        reason: 'empty',
        id: Math.random() * 1000,
        file: file,
      });
      return true;
    } else if (file.size > MAX_SIZE) {
      files.value.push({
        status: 'error',
        // Distinct from the `too_big` a 413 comes back as: this one was measured
        // against `maxFileLimit` here, so its copy can name that number.
        reason: 'over_limit',
        id: Math.random() * 1000,
        file: file,
      });
      return true;
    } else {
      return false;
    }
  });
}
/**
 * Function to handle the selected file(s) and update the files list.
 * @param {Event} event - The file input change event.
 */
async function handleFile(event: Event, isDrag = false) {
  // The dropzone paints itself blocked while disabled but still receives the drop, and
  // `pickFile` is not the only way into here.
  if (props.disable) return;

  let eventFiles: { id: number; file: File }[];
  if (isDrag) {
    // convert current files into allFiles array to be able to add new files
    if (!(event as DragEvent)?.dataTransfer?.files) return;

    // add new files to allFiles array
    eventFiles = Array.from(
      (event as DragEvent).dataTransfer?.files as FileList
    ).map((file) => {
      const id = Math.random() * 1000;
      return {
        id: id,
        file: file,
      };
    });
  } else {
    const targetEvent = event.target as HTMLInputElement;
    // convert current files into allFiles array to be able to add new files
    if (!targetEvent.files) return;

    if (targetEvent.files !== null && checkSizeValid(targetEvent.files)) {
      return;
    }
    // add new files to allFiles array
    eventFiles = Array.from(
      (event.target as HTMLInputElement).files as FileList
    ).map((file) => {
      const id = Math.random() * 1000;
      return {
        id: id,
        file: file,
      };
    });
  }

  // A single-file slot cannot police the drop through the input's `multiple` attribute,
  // which only governs the picker.
  if (!props.multiple) eventFiles = eventFiles.slice(0, 1);

  eventFiles.map(async (fileObject) => {
    files.value.push({
      status: 'pending',
      reason: '',
      id: fileObject.id,
      file: fileObject.file,
    });
  });
  // start new files upload
  await Promise.all(
    eventFiles.map(async (fileObject) => {
      await new Promise((resolve) => {
        emit('createFile', fileObject.file, async (response) => {
          const index = files.value.findIndex((f) => f.id === fileObject.id);
          // The row may have been removed mid-flight (e.g. cancelled).
          if (index === -1) {
            resolve(true);
            return;
          }
          files.value[index] = {
            ...files.value[index],
            status: response.status ? 'success' : 'error',
            reason: response.reason,
            // `id` is the v-for key, so it is never restamped from the response —
            // that would remount the row. The backend id goes in `serverId`.
            serverId: response.serverId,
            file: fileObject.file,
          };
          resolve(true);
        });
      });
    })
  ).then(() => {
    if (uploader.value) {
      uploader.value.value = '';
    }
  });
}

function handleDownload(fileObject: MeridiaFileType) {
  emit('downloadFile', fileObject, async (response) => {
    if (!response) {
      const index = files.value.findIndex((f) => f.id === fileObject.id);
      if (index !== -1) {
        files.value[index].status = 'error';
        files.value[index].reason = 'download_failed';
      }
    }
  });
}

function cancelUpload(fileObject: MeridiaFileType) {
  emit('cancelUpload', fileObject, async (response) => {
    if (response) {
      const index = files.value.findIndex((f) => f.id === fileObject.id);
      if (index > -1) {
        files.value.splice(index, 1);
      }
    }
  });
}

/**
 * Function to delete a file from the files list. Always emits `deleteFile`; it is up
 * to the consumer whether the row is server-addressable (see `serverId`).
 * @param {File} file - The file to be deleted.
 */
async function deleteFile(e: Event, fileObject: MeridiaFileType) {
  e.stopPropagation();
  // A second click before the first delete answers would send a duplicate request.
  if (!files.value || deletingFileIds.has(fileObject.id)) return;
  deletingFileIds.add(fileObject.id);
  emit('deleteFile', fileObject, async (response) => {
    deletingFileIds.delete(fileObject.id);
    const index = files.value.findIndex((f) => f.id === fileObject.id);
    // The row may already be gone (e.g. the consumer refreshed the list).
    if (index === -1) return;
    if (response) {
      files.value.splice(index, 1);
    } else {
      files.value[index].status = 'error';
      files.value[index].reason = 'delete_failed';
    }
  });
}

function humanStorageSize(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i]
  );
}
</script>

<style lang="scss" scoped>
/* Local `fade`, so this component doesn't depend on the global one. */
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease-out;
}
</style>
